import * as yup from 'yup'
import { noodlesAddressSchema, noodlesRemittanceInstructionsSchema } from './noodles-order.js'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesDeliveryNoteLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  itemWeight = 'itemWeight', // weight of the item
}

export const noodlesDeliveryNoteFileDataSchema = yup.object().shape({
  deliveryNoteNumber: yup.string().nullable().optional(),
  date: yup.string().nullable().optional(),
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  deliveryAddress: yup
    .object()
    .shape({
      companyName: yup.string().nullable().optional(),
      address: noodlesAddressSchema.nullable().optional(),
      firstName: yup.string().nullable().optional(),
      lastName: yup.string().nullable().optional(),
      phoneNumber: yup.string().nullable().optional(),
      emailAddress: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  shippingMethod: yup.string().nullable().optional(),
  weight: yup.string().nullable().optional(),
  weightUnit: yup.string().nullable().optional(),
  customerNumber: yup.string().nullable().optional(),
  invoiceNumber: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        itemWeight: yup.string().nullable().optional(),
      }),
    )
    .required(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  showBankDetails: yup.string().nullable().optional(),
})

export type NoodlesDeliveryNoteFileData = yup.InferType<typeof noodlesDeliveryNoteFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesDeliveryNoteFileData>

export const noodlesDeliveryNoteFileMapping: RequiredFileData = {
  deliveryNoteNumber: 'e62ce63c-9f76-4534-9a43-96ad0acea8c2',
  date: 'c8e5d614-cc01-4ac2-a101-0d9a6597befc',
  issuer: {
    companyName: 'b4984f10-3bea-43f7-9ab0-c63ecf0c56a5',
    address: {
      companyName: '75e6363b-545d-4ee7-bbbd-207cafbe0ae4',
      addressLine1: '056dd3d1-0126-43c0-ba43-6f47b3032771',
      addressLine2: '180edd0b-29c4-4aec-86d1-1d20c2b80b9d',
      postalCode: '13dd3236-3352-45e9-8f3c-442cef5db60a',
      city: 'fea15a7a-6543-4bf6-acf2-fcb8dd96cf10',
      country: 'fa888bf4-aa8a-4084-bc8d-3c5418fb50e7',
    },
    firstName: '9d3ab726-b581-4f1e-9898-38afd24c6779',
    lastName: '8c2fc088-c7a2-482b-bfcc-24e0a42653e9',
    phoneNumber: '4c54ccfc-f00d-4434-8b2b-61812488237c',
    emailAddress: 'ca31e6d3-9424-4009-abf9-b168dc986095',
  },
  deliveryAddress: {
    companyName: '4c365a9f-7fcb-4068-892c-be5fcc53cfaa',
    address: {
      companyName: '30b205de-f572-410d-9b9a-e64cde770ae2',
      addressLine1: '3dde31e2-a03d-4746-913f-c00188af6990',
      addressLine2: '52d3ac3d-762e-4468-8fab-556aa37c5355',
      postalCode: '91cd9adf-1371-41d0-aca3-8dc8687a4baa',
      city: '1055f21c-4e61-420b-9d51-d1611d77b1b8',
      country: '2466e524-6f2b-455c-a882-001c7cf84657',
    },
    firstName: '534fcaac-e5e3-4852-b60b-f0cee30ea30a',
    lastName: '383a2a38-b453-4e9b-87a0-52db2c355d2a',
    phoneNumber: '6c5c7193-8252-4497-97e3-3b63acbedfe3',
    emailAddress: '230104e3-74f7-44ef-b81d-699040abc7de',
  },
  shippingMethod: 'f08d0126-d73c-49d6-bd0e-c32d58de3a43',
  weight: '55fb2e58-dba5-4286-8ccb-6d449e3ed244',
  weightUnit: '7be66e2b-e0cf-4f0c-8294-afb537b595e7',
  customerNumber: 'e6551032-c85d-4714-a274-13df4a8e5c87',
  invoiceNumber: '53d30d6b-cd74-404b-9a79-d321d49eeb03',
  externalNote: '254148c4-a718-474a-b0f6-9b3ac2bd81a5',
  items: [
    {
      itemNumber: null,
      itemName: 'a26e15fe-cfad-4e25-9b57-aa9084417c7d',
      description: '2b03c345-df00-4bd3-aa2c-87c64aa485a4',
      itemQty: 'f829527a-7621-45da-965e-05d2028acff3',
      itemWeight: '1b2a4139-b3d5-4288-9546-0248de9e7e3c',
    },
  ],
  agreement: '906326ab-33a0-4101-a6d5-6aa93d8e602f',
  instructions: '5114968e-b66f-4d3c-8954-b21e19c796ed',
  remittanceInstructions: {
    bankName: '205bd3ad-0ee8-4a66-8622-8a1c994f3a95',
    bankAddress: {
      addressLine1: '4b8cef8c-0edc-46d6-9b77-51f8413dbb46',
      addressLine2: 'b88dddfa-e0bd-408d-bb52-052d48e0d149',
      postalCode: '7e6a3238-206f-4ccb-989e-239e13489aed',
      city: '20c2097d-fd5c-43ce-affd-a1fcaa8501a8',
      country: '9aa4c6ae-637d-4a94-b47a-4b474b9ef7d3',
    },
    kto: 'c4fcf2d2-4487-4f28-ab21-fe5fff546181',
    blz: 'f7f5049b-0e9a-41ae-8e10-d39db0dd8a54',
    iban: '84f3741c-b022-48b9-a495-2b05b8f15a4c',
    swift: 'ef25fb99-b19f-4d83-b776-bc4ce2061687',
    firstName: 'f84e724d-6aac-4798-9d33-874881ff2d0d',
    lastName: '9d49c055-2506-4410-9108-8fd4f1a87ff5',
    ustIdNr: '47fbeac4-5fa8-47f9-9cf1-5e79e9f9ebb5',
    beneficiaryName: '655f1fa9-fef4-4a91-bf9c-b92f45673bf2',
    beneficiaryAddress: {
      addressLine1: 'f929cb48-b8ec-4c33-b641-481bb943bd1e',
      addressLine2: '1dc84b4e-499b-4088-b4fa-d89b7388d225',
      postalCode: 'd40c9242-3012-4552-bcc2-45665567085a',
      city: '9a48470b-a39b-49ae-935d-dd713be194db',
      country: 'b3f2542f-7d00-47dd-870b-6667c66af899',
    },
    telephone: 'fe7a3d5c-de2b-426a-990b-e7b31c224a55',
    telefax: 'da4aed25-ae03-44fd-bbd8-20bc7954bc29',
    email: '786dc573-303d-42c7-bfb6-e5db04a4bfc0',
    website: 'bb599182-abe0-40e9-b93b-6afaab6e7fe6',
    paymentLink: '074e36f6-71a8-4ccd-a9b7-0972fade95de',
  },
  showBankDetails: '1b5c80d2-ad71-4c0f-abdb-8a464afc9c3f',
}
